
import DeleteCasePopUp from "@/components/Popups/DeleteCasePopUp.vue";
import { HIGH_RISK_ELIGIBILITY_RESULT } from "@/constants/assessment";
import { ERROR_NOTIFICATION_TYPE, SUCCESS_NOTIFICATION_TYPE } from "@/constants/notification";
import { ELIGIBILITY_CHECK_SERVICE_TYPE, SPONSOR_LICENCE_SERVICE_TYPE } from "@/constants/services";
import { API_TCase_Filter, TCase_Table } from "@/models/cases_expert_cases";
import { Component, Prop, Vue } from "vue-property-decorator";

const DELETE_CASE_ACTION_TYPE = "deleteCase";
const DELETE_CASE_PLUS_CANDIDATE_PROFILE_ACTION_TYPE = "deleteCasePlusCandidateProfile";
const DELETE_CASE_PLUS_EC_ACTION_TYPE = "deleteCasePlusEC";

type ParamsKeyObject = {
  [key: string]: any;
};
@Component({
  components: {
    DeleteCasePopUp,
  },
})
export default class ExpertServicesTable extends Vue {
  @Prop({ type: Array, default: () => ({}) }) readonly items?: TCase_Table[];
  @Prop({ type: Object, default: () => ({}) }) readonly params?: ParamsKeyObject;
  @Prop({ type: Object, default: () => ({}) }) readonly filters?: API_TCase_Filter;
  @Prop({ type: Boolean, default: false }) readonly loading?: boolean;

  get tableProps() {
    const { loading, headers, items, filters, labels, params } = this;

    return {
      loading,
      headers,
      items,
      filters,
      labels,
      params,
    };
  }

  options = {
    infiniteScroll: true,
    filtersApi: true,
    sortApi: true,
  };

  labels = {
    allLabel: this.$t("table.allLabel"),
    placeholderLabel: this.$t("table.placeholderLabel"),
  };

  headers = [
    {
      text: this.$t("table.headers.id"),
      value: "id",
      sortable: true,
      filterable: false,
      align: "start",
      sortBy: "number",
    },
    {
      text: this.$t("table.headers.category"),
      value: "case_type",
      filterable: true,
    },
    {
      text: this.$t("table.headers.extension"),
      value: "is_extension",
      slot: true,
      filterable: true,
    },
    {
      text: this.$t("table.headers.childCase"),
      value: "has_child_case",
      slot: true,
      filterable: true,
    },
    {
      text: this.$t("table.headers.companyName"),
      value: "company_name",
      filterable: true,
      width: "70",
    },
    {
      text: this.$t("table.headers.applicantName"),
      value: "name",
      filterable: true,
    },
    {
      text: this.$t("table.headers.submissionDate"),
      value: "submission_date",
      sortable: true,
      sortBy: "date",
      width: "120",
    },
    {
      text: this.$t("table.headers.resubmissionDate"),
      value: "resubmission_date",
      sortable: true,
      sortBy: "date",
      width: "110",
    },
    {
      text: this.$t("table.headers.timeElapsed"),
      value: "time_elapsed",
      sortable: true,
      sortBy: "time",
      width: "60",
    },
    {
      text: this.$t("table.headers.step"),
      value: "case_progress_step_name",
      filterable: true,
    },
    {
      text: this.$t("table.headers.finalDecision"),
      value: "final_decision",
      filterable: true,
      width: "55",
      error: HIGH_RISK_ELIGIBILITY_RESULT,
    },
  ];

  loadData(payload: any) {
    this.$emit("load-data", payload);
  }

  goToCase(item: TCase_Table) {
    const { id } = item;

    const routeData = this.$router.resolve(`cases/${id}/dashboard/0`);

    window.open(routeData.href, "_blank");
  }

  copyToClipboard(text: string) {
    if (!navigator.clipboard) return;

    let type: string;
    let title: string;

    try {
      navigator.clipboard.writeText(text);

      type = SUCCESS_NOTIFICATION_TYPE;
      title = `"${text}" ${this.$t("copied")}`;
    } catch (err) {
      type = ERROR_NOTIFICATION_TYPE;
      title = err as string;
    }

    this.$root.$emit("show-notification", {
      type,
      title,
    });
  }

  targetId = "";
  actionType = "";
  showDeletePopup = false;

  deleteCase(id) {
    this.targetId = id;

    this.actionType = DELETE_CASE_ACTION_TYPE;

    this.showDeletePopup = true;
  }

  deleteCasePlusCandidateProfile(id) {
    this.targetId = id;

    this.actionType = DELETE_CASE_PLUS_CANDIDATE_PROFILE_ACTION_TYPE;

    this.showDeletePopup = true;
  }

  deleteCasePlusEC(id) {
    this.targetId = id;

    this.actionType = DELETE_CASE_PLUS_EC_ACTION_TYPE;

    this.showDeletePopup = true;
  }

  deleteCancel() {
    this.targetId = "";
    this.showDeletePopup = false;
  }

  deleteSuccess() {
    const { params } = this;

    // DEV-9133. We need make full refresh to avoid list errors;
    Object.assign(params, {
      offset: 0,
    });

    this.$emit("load-data", { params, isLimit: false });

    this.deleteCancel();
  }

  get deletePopUpProps() {
    const { targetId, actionType, showDeletePopup } = this;

    return {
      show: showDeletePopup,
      action: actionType,
      serviceId: targetId,
    };
  }

  checkIfEnableToDeleteCasePlusCandidateProfile(item?: TCase_Table) {
    if (!item) return false;

    const { case_type, is_extension } = item;

    if (!case_type || is_extension) return false;

    return ![SPONSOR_LICENCE_SERVICE_TYPE].includes(case_type);
  }

  checkIfEnableDeleteCases(item) {
    const { case_type, is_extension } = item;

    if (!case_type) return false;

    if (is_extension) return true;

    return ![ELIGIBILITY_CHECK_SERVICE_TYPE].includes(case_type);
  }

  checkIfEnableToDeleteCasePlusEC(item?: TCase_Table) {
    if (!item) return false;

    const { case_type, is_extension } = item;

    if (!case_type || !is_extension) return false;

    return ![SPONSOR_LICENCE_SERVICE_TYPE, ELIGIBILITY_CHECK_SERVICE_TYPE].includes(case_type);
  }
}
